import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  FormLabel,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  Button,
  Grid,
  Paper,
  Alert,
  CircularProgress,
  InputLabel,
  FormHelperText,
  IconButton,
  InputAdornment
} from '@mui/material';
import { PictureAsPdf as PdfIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enGB } from 'date-fns/locale';
import { Form, FormSection, FormField, FormFieldType, FormFieldDataType, FormFieldDataItem, formService } from '../../services/formService';
import { PDFService } from '../../services/pdfService';

interface FormRendererProps {
  form: Form;
  jobId?: string;
  jobNumber?: string;
  customerId?: string;
  assignedSalesPersonName?: string; // DisplayName of the sales person for the job
  job?: any; // Full job object for parameter injection
  customer?: any; // Full customer object for parameter injection
  onSubmit?: (responseData: any) => void;
  onCancel?: () => void;
  readOnly?: boolean;
  existingResponse?: any;
  isLoadingResponses?: boolean;
}

const FormRenderer: React.FC<FormRendererProps> = ({
  form,
  jobId,
  jobNumber,
  customerId,
  assignedSalesPersonName,
  job,
  customer,
  onSubmit,
  onCancel,
  readOnly = false,
  existingResponse,
  isLoadingResponses = false
}) => {
  const [formData, setFormData] = useState<{ [key: string]: any }>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  useEffect(() => {
    if (existingResponse) {
      try {
        const parsedData = typeof existingResponse === 'string' 
          ? JSON.parse(existingResponse) 
          : existingResponse;
        setFormData(parsedData);
        setIsDataLoaded(true);
      } catch (err) {
        console.error('Failed to parse existing response:', err);
      }
    } else if (!isLoadingResponses) {
      // If we're not loading and there's no existing response, data is loaded (might be empty)
      setIsDataLoaded(true);
    }
  }, [existingResponse, isLoadingResponses]);

  // Reset data loaded state when loading starts
  useEffect(() => {
    if (isLoadingResponses) {
      setIsDataLoaded(false);
    }
  }, [isLoadingResponses]);

  // Inject parameter values and default values into form fields
  useEffect(() => {
    if (isDataLoaded && form?.formSections) {
      const newFormData = { ...formData };

      form.formSections.forEach((section: any) => {
        section.formFields?.forEach((field: any) => {
          const parameterValue = getParameterValue(field);
          if (parameterValue !== null && !formData[field.code]) {
            // Only inject if field doesn't already have a value
            newFormData[field.code] = parameterValue;
          } else {
            // Check for default value if no parameter value was injected
            const config = formService.parseConfigurationJson(field.configurationJson);
            if (config?.defaultValue !== undefined && config?.defaultValue !== null && !formData[field.code]) {
              // Only set default value if field doesn't already have a value
              newFormData[field.code] = config.defaultValue;
            }
          }
        });
      });

      if (Object.keys(newFormData).length > Object.keys(formData).length) {
        setFormData(newFormData);
      }
    }
  }, [isDataLoaded, form, job, customer]);

  const handleFieldChange = (fieldCode: string, value: any) => {
    setFormData(prev => {
      const newData = {
        ...prev,
        [fieldCode]: value
      };

      // Check if the changed field has reverse calculation configuration
      // This allows fields like TOTAL_INCL_GST to calculate PRICE and GST backwards
      if (form?.formSections) {
        form.formSections.forEach((section: any) => {
          section.formFields?.forEach((field: any) => {
            if (field.code === fieldCode) {
              const config = formService.parseConfigurationJson(field.configurationJson);
              if (config?.reverseCalculate) {
                // reverseCalculate is an object mapping field codes to formulas
                // e.g., { "PRICE": "TOTAL_INCL_GST / 1.15", "GST": "PRICE * 0.15" }
                const reverseCalculations = config.reverseCalculate;
                const precision = config.reverseCalculatePrecision || 2;
                
                try {
                  // Calculate each reverse field in order (formulas may depend on each other)
                  const calculatedFields: string[] = [];
                  const reverseData = { ...newData };
                  
                  // Process reverse calculations - may need multiple passes if fields depend on each other
                  let maxIterations = 10; // Prevent infinite loops
                  let changed = true;
                  
                  while (changed && maxIterations > 0) {
                    changed = false;
                    maxIterations--;
                    
                    for (const [targetFieldCode, formula] of Object.entries(reverseCalculations)) {
                      if (calculatedFields.includes(targetFieldCode)) continue; // Already calculated
                      
                      // Replace field codes in formula with actual values from reverseData
                      let processedFormula = formula as string;
                      let canCalculate = true;
                      
                      // Extract all field codes from the formula (simple regex to find word-like patterns)
                      const fieldCodePattern = /\b[A-Z_][A-Z0-9_]*\b/g;
                      const fieldCodesInFormula = processedFormula.match(fieldCodePattern) || [];
                      
                      // Check if all required fields are available (excluding the target field itself)
                      for (const codeInFormula of fieldCodesInFormula) {
                        if (codeInFormula === targetFieldCode) continue; // Skip the target field itself
                        
                        const val = reverseData[codeInFormula];
                        if (val === undefined || val === null || val === '') {
                          canCalculate = false;
                          break;
                        }
                      }
                      
                      if (canCalculate) {
                        // Replace all field codes with their values
                        for (const codeInFormula of fieldCodesInFormula) {
                          if (codeInFormula === targetFieldCode) continue; // Don't replace the target field
                          const val = reverseData[codeInFormula];
                          if (val !== undefined && val !== null && val !== '') {
                            processedFormula = processedFormula.replace(new RegExp(`\\b${codeInFormula}\\b`, 'g'), val?.toString() || '0');
                          }
                        }
                        
                        try {
                          const result = eval(processedFormula);
                          if (typeof result === 'number' && !isNaN(result)) {
                            const roundedResult = Math.round(result * Math.pow(10, precision)) / Math.pow(10, precision);
                            reverseData[targetFieldCode] = roundedResult;
                            newData[targetFieldCode] = roundedResult;
                            calculatedFields.push(targetFieldCode);
                            changed = true;
                          }
                        } catch (e) {
                          console.error(`Error calculating reverse field ${targetFieldCode}:`, e);
                        }
                      }
                    }
                  }
                } catch (error) {
                  console.error(`Error in reverse calculation for ${fieldCode}:`, error);
                }
              }
            }
          });
        });
      }

      // Calculate dependent fields using the updated data
      if (form?.formSections) {
        form.formSections.forEach((section: any) => {
          section.formFields?.forEach((field: any) => {
            const config = formService.parseConfigurationJson(field.configurationJson);
            if (config?.calculatedFrom?.fields?.includes(fieldCode)) {
              // Calculate using newData instead of old formData
              const calculatedValue = calculateFieldValueWithData(field, newData);
              if (calculatedValue !== null) {
                // Always auto-update calculated fields when their dependencies change
                // The field remains editable, so user can override if needed
                newData[field.code] = calculatedValue;
              }
            }
          });
        });
      }

      return newData;
    });
  };

  const handleSubmit = async () => {
    if (!onSubmit || !jobId) {
      setError('Job ID is required to submit form');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      await formService.submitFormResponse(form, jobId, formData);
      setSuccess('Form submitted successfully');
      onSubmit(formData);
    } catch (err: any) {
      setError(err.message || 'Failed to submit form');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateInteractivePDF = async () => {
    // Wait for form responses to load if they're still loading
    if (isLoadingResponses) {
      setError('Please wait for form data to load before downloading PDF');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Get form sections (not individual fields)
      const formSections = form.formSections || [];

      // Use existingResponse directly if available and has data, otherwise use formData state
      // This ensures we have the latest data even if state hasn't updated yet
      let dataToUse = formData;
      if (existingResponse) {
        try {
          const parsedData = typeof existingResponse === 'string' 
            ? JSON.parse(existingResponse) 
            : existingResponse;
          // Only use existingResponse if it actually has data (not empty object)
          if (parsedData && typeof parsedData === 'object' && Object.keys(parsedData).length > 0) {
            dataToUse = parsedData;
          }
        } catch (err) {
          console.warn('Failed to parse existingResponse for PDF, using formData state:', err);
          // Fall back to formData state if parsing fails
        }
      }

      console.log('Generating interactive PDF with form sections:', formSections);
      console.log('FormRenderer: formData state:', formData);
      console.log('FormRenderer: existingResponse:', existingResponse);
      console.log('Form data being used:', dataToUse);
      console.log('FormRenderer: Full form object:', form);
      console.log('FormRenderer: form.headerConfigurationJson:', form.headerConfigurationJson);
      console.log('FormRenderer: form.headerImageBase64:', form.headerImageBase64);
      console.log('FormRenderer: form.pdfConfigurationJson:', form.pdfConfigurationJson);

      // Generate filename based on job number and form name: {jobNumber}-{form-name}.pdf
      const sanitizedFormName = form.description.replace(/[^a-zA-Z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
      const sanitizedJobNumber = jobNumber ? jobNumber.replace(/[^a-zA-Z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '') : 'job';
      const filename = `${sanitizedJobNumber}-${sanitizedFormName}.pdf`;

      // Parse PDF configuration from form if available
      let pdfConfig: any = {};
      if (form.pdfConfigurationJson) {
        try {
          pdfConfig = JSON.parse(form.pdfConfigurationJson);
        } catch (error) {
          console.warn('Failed to parse PDF configuration JSON:', error);
        }
      }

      // Parse header configuration from form if available
      let headerConfig: any = null;
      if (form.headerConfigurationJson) {
        try {
          headerConfig = JSON.parse(form.headerConfigurationJson);
          console.log('FormRenderer: Parsed headerConfig:', headerConfig);
        } catch (error) {
          console.warn('Failed to parse header configuration JSON:', error);
        }
      } else {
        console.log('FormRenderer: form.headerConfigurationJson is null/undefined');
      }

      // Prepare header data for placeholder replacement (e.g., {{DisplayName}})
      const headerData: { [key: string]: string | number | boolean | null | undefined } = {
        DisplayName: assignedSalesPersonName || ''
      };

      // Generate truly interactive PDF with editable fields using pdf-lib
      // Use the latest data (existingResponse if available, otherwise formData)
      await PDFService.generateEditablePDF(dataToUse, form.description, formSections, {
        filename,
        headerImageBase64: form.headerImageBase64,
        headerImageUrl: form.headerImageBase64,
        headerConfigurationJson: form.headerConfigurationJson,
        headerHeight: pdfConfig.headerHeight ?? 100,
        pageMargin: pdfConfig.pageMargin ?? 15,
        headerData,
        // Form layout configuration
        numberOfColumns: form.numberOfColumns,
        // Font size configuration
        defaultFontSize: pdfConfig.defaultFontSize,
        labelFontSize: pdfConfig.labelFontSize,
        multilineFontSize: pdfConfig.multilineFontSize,
        sectionTitleFontSize: pdfConfig.sectionTitleFontSize,
        // Padding and spacing configuration
        fieldSpacing: pdfConfig.fieldSpacing,
        sectionSpacing: pdfConfig.sectionSpacing,
        checkboxListSpacing: pdfConfig.checkboxListSpacing,
        sectionPadding: pdfConfig.sectionPadding,
        fieldHeight: pdfConfig.fieldHeight,
        showSectionBorders: pdfConfig.showSectionBorders
      });

      setSuccess('Downloaded PDF');
    } catch (err: any) {
      console.error('Error generating PDF:', err);
      setError(err.message || 'Failed to generate PDF');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to normalize fieldType (handle both enum and string values from API)
  const normalizeFieldType = (fieldType: FormFieldType | string): FormFieldType => {
    // If it's already a number (enum value), return it
    if (typeof fieldType === 'number') {
      return fieldType as FormFieldType;
    }
    
    // If it's a string, convert to enum value
    const typeMap: { [key: string]: FormFieldType } = {
      'String': FormFieldType.String,
      'Multiline': FormFieldType.Multiline,
      'Bool': FormFieldType.Bool,
      'Dimension': FormFieldType.Dimension,
      'Int': FormFieldType.Int,
      'Dropdown': FormFieldType.Dropdown,
      'Date': FormFieldType.Date,
      'DateTime': FormFieldType.DateTime,
      'CheckboxList': FormFieldType.CheckboxList,
      'HorizontalRule': FormFieldType.HorizontalRule
    };
    
    return typeMap[fieldType as string] || FormFieldType.String;
  };

  // Helper function to check if a field should be enabled based on enabledWhen configuration
  const isFieldEnabled = (field: FormField, config: any): boolean => {
    // Debug: Log all fields with enabledWhen to see what's being checked
    if (config?.enabledWhen) {
      console.log(`[DEBUG] isFieldEnabled called for field: ${field.code}, config:`, JSON.stringify(config));
    }
    
    // If readOnly, respect that first
    if (readOnly) {
      return false;
    }

    // Check for conditional enablement
    if (config?.enabledWhen) {
      const { fieldCode, value: requiredValue, operator } = config.enabledWhen;
      console.log(`[DEBUG] enabledWhen found for ${field.code}: fieldCode=${fieldCode}, operator=${operator}, requiredValue=${requiredValue}`);
      
      if (fieldCode) {
        // Check if the key exists in formData (even if value is undefined)
        const hasKey = fieldCode in formData;
        const dependentFieldValue = formData[fieldCode];
        
        // Handle "exists" operator - check if field has a value (is truthy and not empty string)
        // For DateTime fields: when set, value is an ISO string; when cleared, it's undefined or empty string
        if (operator === 'exists') {
          // Debug logging (can be removed after testing)
          if (fieldCode === 'CLIENT_PRESENTATION_DATE') {
            console.log(`[DEBUG] Checking if ${fieldCode} exists. HasKey: ${hasKey}, Value:`, dependentFieldValue, 'Type:', typeof dependentFieldValue, 'formData keys:', Object.keys(formData));
          }
          
          // Check if value exists and is not empty
          // Handle undefined, null, empty string, and whitespace-only strings
          // Also check if key doesn't exist in formData at all
          if (!hasKey || dependentFieldValue === undefined || dependentFieldValue === null || dependentFieldValue === '') {
            if (fieldCode === 'CLIENT_PRESENTATION_DATE') {
              console.log(`[DEBUG] ${fieldCode} is empty/undefined/null/missing - returning false`);
            }
            return false;
          }
          // For strings, check if not empty after trimming
          if (typeof dependentFieldValue === 'string') {
            const trimmed = dependentFieldValue.trim();
            // For DateTime fields, an ISO string should be at least 10 characters (YYYY-MM-DD)
            const result = trimmed.length > 0;
            if (fieldCode === 'CLIENT_PRESENTATION_DATE') {
              console.log(`[DEBUG] ${fieldCode} is string, trimmed length: ${trimmed.length}, result: ${result}`);
            }
            return result;
          }
          // For other types (Date objects, numbers, booleans, etc.), check if truthy
          const result = !!dependentFieldValue;
          if (fieldCode === 'CLIENT_PRESENTATION_DATE') {
            console.log(`[DEBUG] ${fieldCode} is not string, truthy check: ${result}`);
          }
          return result;
        }
        
        // Handle exact value comparison (default behavior)
        if (requiredValue !== undefined) {
          // Compare values - handle boolean, string, number, etc.
          if (typeof requiredValue === 'boolean') {
            return dependentFieldValue === requiredValue;
          } else if (typeof requiredValue === 'string') {
            return dependentFieldValue === requiredValue;
          } else if (typeof requiredValue === 'number') {
            return dependentFieldValue === requiredValue;
          } else {
            // For other types, use strict equality
            return dependentFieldValue === requiredValue;
          }
        }
      }
    }

    // If no conditional enablement, field is enabled (unless readOnly)
    return true;
  };

  // Helper function to inject parameter values into fields
  const getParameterValue = (field: FormField): any => {
    const config = formService.parseConfigurationJson(field.configurationJson);
    if (config?.parameterValue) {
      const { source, property } = config.parameterValue;
      let dataSource: any = null;

      switch (source) {
        case 'job':
          dataSource = job;
          break;
        case 'customer':
          dataSource = customer;
          break;
        case 'props':
          // Could be extended to support other prop sources
          break;
        default:
          console.warn(`Unknown parameter source: ${source}`);
          return null;
      }

      if (dataSource && property && dataSource[property] !== undefined) {
        return dataSource[property];
      }
    }
    return null;
  };

  // Helper function to calculate field values based on other fields (with custom data)
  const calculateFieldValueWithData = (field: FormField, data: { [key: string]: any }): any => {
    const config = formService.parseConfigurationJson(field.configurationJson);
    if (config?.calculatedFrom) {
      const { fields, formula, precision = 2 } = config.calculatedFrom;

      // Check if all required fields have values
      const fieldValues: { [key: string]: any } = {};
      for (const fieldCode of fields) {
        const value = data[fieldCode];
        if (value === undefined || value === null || value === '') {
          return null; // Cannot calculate if any required field is empty
        }
        // Convert to number if possible
        const numValue = parseFloat(value);
        fieldValues[fieldCode] = isNaN(numValue) ? value : numValue;
      }

      try {
        // Replace field codes in formula with actual values
        let processedFormula = formula;
        for (const [fieldCode, value] of Object.entries(fieldValues)) {
          processedFormula = processedFormula.replace(new RegExp(fieldCode, 'g'), value.toString());
        }

        // Evaluate the formula
        const result = eval(processedFormula);

        // Round to specified precision
        if (typeof result === 'number') {
          return Math.round(result * Math.pow(10, precision)) / Math.pow(10, precision);
        }

        return result;
      } catch (error) {
        console.error(`Error calculating field ${field.code}:`, error);
        return null;
      }
    }
    return null;
  };

  // Helper function to calculate field values based on other fields (uses current formData)
  const calculateFieldValue = (field: FormField): any => {
    return calculateFieldValueWithData(field, formData);
  };

  const renderField = (field: FormField, reserveLabelSpace: boolean = false) => {
    const config = formService.parseConfigurationJson(field.configurationJson);
    const prefixAdornment = config.prefix
      ? <InputAdornment position="start">{config.prefix}</InputAdornment>
      : undefined;
    const suffixAdornment = config.suffix
      ? <InputAdornment position="end">{config.suffix}</InputAdornment>
      : undefined;
    const adornmentProps = prefixAdornment || suffixAdornment
      ? {
          ...(prefixAdornment ? { startAdornment: prefixAdornment } : {}),
          ...(suffixAdornment ? { endAdornment: suffixAdornment } : {})
        }
      : undefined;

    // Debug: Log fields that should have conditional enablement
    if (field.code === 'PRESENTATION_COMPLETED' || field.code === 'SPEC_SHEET_SIGNED' ||
        field.code === 'CONTRACT_SIGNED' || field.code === 'DEPOSIT_RECEIVED' ||
        field.code === 'KITCHEN_SOLD') {
      console.log(`[DEBUG] renderField for ${field.code}:`, {
        configurationJson: field.configurationJson,
        parsedConfig: config,
        hasEnabledWhen: !!config?.enabledWhen
      });
    }

    // Get field value - priority: user entered > calculated > default > empty
    const value = formData[field.code] || '';
    const calculatedValue = calculateFieldValue(field);
    const hasCalculatedValue = calculatedValue !== null;
    const hasDefaultValue = config?.defaultValue !== undefined && config?.defaultValue !== null;

    // Use user's value if they've entered one, otherwise use calculated value, then default value
    const fieldValue = value !== '' ? value :
                      (hasCalculatedValue ? calculatedValue :
                       (hasDefaultValue ? config.defaultValue : ''));

    const isRequired = field.isRequired && !readOnly;
    const normalizedFieldType = normalizeFieldType(field.fieldType);
    // Allow calculated fields to remain editable - user can override calculated values
    const isEnabled = isFieldEnabled(field, config);

    // Check if field has a description
    const hasDescription = field.description && field.description.trim() !== '';

    switch (normalizedFieldType) {
      case FormFieldType.String:
        const stringLabelAlign = formService.getContextValue(config, 'labelTextAlign', 'web') || 'left';
        return (
          <Box key={field.id}>
            {hasDescription && (
              <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, textAlign: stringLabelAlign }}>
                {field.description}{isRequired ? ' *' : ''}
              </Typography>
            )}
            {reserveLabelSpace && !hasDescription && (
              <Box sx={{ mb: 0.5, height: '20px' }} />
            )}
            <TextField
              fullWidth
              size="small"
              value={fieldValue ?? ''}
              onChange={(e) => handleFieldChange(field.code, e.target.value)}
              required={isRequired}
              disabled={!isEnabled}
              placeholder={config.placeholder}
              inputProps={{ maxLength: config.maxLength }}
              helperText={field.info}
              InputProps={adornmentProps}
            />
          </Box>
        );

      case FormFieldType.Multiline:
        // Check if field should be on next line
        if (config.fieldOnNextLine) {
          const labelAlign = formService.getContextValue(config, 'labelTextAlign', 'web') || 'left';
          return (
            <Box key={field.id} sx={{ width: '100%' }}>
              {hasDescription && (
                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, textAlign: labelAlign }}>
                  {field.description}
                </Typography>
              )}
              {reserveLabelSpace && !hasDescription && (
                <Box sx={{ mb: 0.5, height: '20px' }} />
              )}
              <TextField
                fullWidth
                multiline
                rows={config.multilineLines || config.rows || 3}
                value={value}
                onChange={(e) => handleFieldChange(field.code, e.target.value)}
                required={isRequired}
                disabled={!isEnabled}
                placeholder={config.placeholder}
                helperText={field.info}
                InputProps={adornmentProps}
              />
            </Box>
          );
        }
        const multilineLabelAlign = formService.getContextValue(config, 'labelTextAlign', 'web');
        return (
          <TextField
            key={field.id}
            fullWidth
            multiline
            rows={config.multilineLines || config.rows || 3}
            label={field.description}
            value={value}
            onChange={(e) => handleFieldChange(field.code, e.target.value)}
            required={isRequired}
            disabled={!isEnabled}
            placeholder={config.placeholder}
            helperText={field.info}
            InputProps={adornmentProps}
            InputLabelProps={multilineLabelAlign ? { sx: { textAlign: multilineLabelAlign } } : undefined}
          />
        );

      case FormFieldType.Bool:
        const boolLabelAlign = formService.getContextValue(config, 'labelTextAlign', 'web') || 'left';
        // If checkbox has no description, add an empty label spacer to align with other fields
        if (!field.description || field.description.trim() === '') {
          return (
            <Box key={field.id}>
              <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, textAlign: boolLabelAlign, visibility: 'hidden' }}>
                &nbsp;
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={value || false}
                    onChange={(e) => handleFieldChange(field.code, e.target.checked)}
                    disabled={!isEnabled}
                  />
                }
                label=""
              />
            </Box>
          );
        }
        return (
          <FormControlLabel
            key={field.id}
            control={
              <Checkbox
                checked={value || false}
                onChange={(e) => handleFieldChange(field.code, e.target.checked)}
                disabled={!isEnabled}
              />
            }
            label={field.description}
          />
        );

      case FormFieldType.Int:
        const intLabelAlign = formService.getContextValue(config, 'labelTextAlign', 'web') || 'left';
        return (
          <Box key={field.id}>
            {field.description && (
              <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, textAlign: intLabelAlign }}>
                {field.description}{isRequired ? ' *' : ''}
              </Typography>
            )}
            <TextField
              fullWidth
              size="small"
              type="number"
              value={value ?? ''}
              onChange={(e) => handleFieldChange(field.code, parseInt(e.target.value) || 0)}
              required={isRequired}
              disabled={!isEnabled}
              helperText={field.info}
              InputProps={adornmentProps}
            />
          </Box>
        );

      case FormFieldType.Dimension:
        // Parse existing dimension value (format: "H:value W:value D:value" or similar)
        const parseDimensionValue = (val: string) => {
          if (!val) return { height: '', width: '', depth: '' };
          const parts = val.split(' ');
          const result = { height: '', width: '', depth: '' };
          parts.forEach(part => {
            if (part.startsWith('H:')) result.height = part.substring(2);
            else if (part.startsWith('W:')) result.width = part.substring(2);
            else if (part.startsWith('D:')) result.depth = part.substring(2);
          });
          return result;
        };

        const dimensionValue = parseDimensionValue(value);
        
        const handleDimensionChange = (dimension: 'height' | 'width' | 'depth', newValue: string) => {
          const updated = { ...dimensionValue, [dimension]: newValue };
          const formattedValue = `H:${updated.height} W:${updated.width} D:${updated.depth}`;
          handleFieldChange(field.code, formattedValue);
        };

        const dimensionLabelAlign = formService.getContextValue(config, 'labelTextAlign', 'web') || 'left';
        return (
          <Box key={field.id}>
            <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, textAlign: dimensionLabelAlign }}>
              {field.description}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="H"
                  value={dimensionValue.height}
                  onChange={(e) => handleDimensionChange('height', e.target.value)}
                  disabled={!isEnabled}
                  placeholder="Height"
                  size="small"
                  type="number"
                  inputProps={{ 
                    step: config.precision ? Math.pow(10, -config.precision) : 0.01,
                    min: config.allowNegative === false ? 0 : undefined
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="W"
                  value={dimensionValue.width}
                  onChange={(e) => handleDimensionChange('width', e.target.value)}
                  disabled={!isEnabled}
                  placeholder="Width"
                  size="small"
                  type="number"
                  inputProps={{ 
                    step: config.precision ? Math.pow(10, -config.precision) : 0.01,
                    min: config.allowNegative === false ? 0 : undefined
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="D"
                  value={dimensionValue.depth}
                  onChange={(e) => handleDimensionChange('depth', e.target.value)}
                  disabled={!isEnabled}
                  placeholder="Depth"
                  size="small"
                  type="number"
                  inputProps={{ 
                    step: config.precision ? Math.pow(10, -config.precision) : 0.01,
                    min: config.allowNegative === false ? 0 : undefined
                  }}
                />
              </Grid>
            </Grid>
            {field.info && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {field.info} {config.unit ? `(${config.unit})` : ''}
              </Typography>
            )}
          </Box>
        );

      case FormFieldType.Dropdown:
        const dropdownLabelAlign = formService.getContextValue(config, 'labelTextAlign', 'web') || 'left';
        return (
          <Box key={field.id}>
            {field.description && (
              <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, textAlign: dropdownLabelAlign }}>
                {field.description}{isRequired ? ' *' : ''}
              </Typography>
            )}
            <FormControl fullWidth required={isRequired} disabled={!isEnabled} size="small">
              <Select
                value={value || ''}
                onChange={(e) => handleFieldChange(field.code, e.target.value)}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  <em>Select...</em>
                </MenuItem>
                {field.formFieldDataType?.formFieldDataItems
                  ?.filter(item => item.isActive)
                  ?.sort((a, b) => a.displayOrder - b.displayOrder)
                  ?.map((item) => (
                    <MenuItem key={item.id} value={item.code}>
                      {item.description}
                    </MenuItem>
                  ))}
              </Select>
              {field.info && <FormHelperText>{field.info}</FormHelperText>}
            </FormControl>
          </Box>
        );

      case FormFieldType.CheckboxList:
        // Value is an array of codes (e.g., ['MELAMINE', 'PANELFORM'])
        const checkboxValue = Array.isArray(value) ? value : (value ? [value] : []);
        
        const handleCheckboxChange = (itemCode: string, checked: boolean) => {
          if (checked) {
            handleFieldChange(field.code, [...checkboxValue, itemCode]);
          } else {
            handleFieldChange(field.code, checkboxValue.filter(code => code !== itemCode));
          }
        };

        const items = field.formFieldDataType?.formFieldDataItems
          ?.filter(item => item.isActive)
          ?.sort((a, b) => a.displayOrder - b.displayOrder) || [];

        // Determine layout: use ConfigurationJson columns property or default to 1
        const checkboxConfig = formService.parseConfigurationJson(field.configurationJson);
        const columns = checkboxConfig.columns || 1;
        const itemsPerColumn = Math.ceil(items.length / columns);

        const checkboxLabelAlign = formService.getContextValue(checkboxConfig, 'labelTextAlign', 'web') || 'left';
        return (
          <Box key={field.id}>
            <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, textAlign: checkboxLabelAlign }}>
              {field.description}
            </Typography>
            <Grid container spacing={1}>
              {Array.from({ length: columns }).map((_, colIndex) => (
                <Grid item xs={12} sm={12 / columns} key={colIndex}>
                  {items.slice(colIndex * itemsPerColumn, (colIndex + 1) * itemsPerColumn).map((item) => (
                    <FormControlLabel
                      key={item.id}
                      control={
                        <Checkbox
                          checked={checkboxValue.includes(item.code)}
                          onChange={(e) => handleCheckboxChange(item.code, e.target.checked)}
                          disabled={!isEnabled}
                        />
                      }
                      label={item.description}
                    />
                  ))}
                </Grid>
              ))}
            </Grid>
            {field.info && (
              <FormHelperText sx={{ mt: 1 }}>
                {field.info}
              </FormHelperText>
            )}
          </Box>
        );

      case FormFieldType.Date:
        const dateLabelAlign = formService.getContextValue(config, 'labelTextAlign', 'web') || 'left';
        return (
          <Box key={field.id}>
            {field.description && (
              <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, textAlign: dateLabelAlign }}>
                {field.description}{isRequired ? ' *' : ''}
              </Typography>
            )}
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
              <DatePicker
                value={value ? new Date(value) : null}
                onChange={(date) => handleFieldChange(field.code, date?.toISOString())}
                disabled={!isEnabled}
                format="dd/MM/yyyy"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    required: isRequired,
                    helperText: field.info
                  }
                }}
              />
            </LocalizationProvider>
          </Box>
        );

      case FormFieldType.DateTime:
        const dateTimeLabelAlign = formService.getContextValue(config, 'labelTextAlign', 'web') || 'left';
        return (
          <Box key={field.id}>
            {field.description && (
              <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, textAlign: dateTimeLabelAlign }}>
                {field.description}{isRequired ? ' *' : ''}
              </Typography>
            )}
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
              <DateTimePicker
                value={value ? new Date(value) : null}
                onChange={(date) => handleFieldChange(field.code, date?.toISOString())}
                disabled={!isEnabled}
                format="dd/MM/yyyy HH:mm"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    required: isRequired,
                    helperText: field.info
                  }
                }}
              />
            </LocalizationProvider>
          </Box>
        );

      case FormFieldType.HorizontalRule:
        // Render a horizontal divider line
        const spacingAfter = config.spacingAfter || 0;
        // Convert PDF points to pixels (1 point = 1.33px approximately, but Material-UI spacing unit = 8px)
        // So spacingAfter in points / 8 gives us Material-UI spacing units
        const spacingAfterUnits = spacingAfter > 0 ? spacingAfter / 8 : 2;
        return (
          <Box 
            key={field.id} 
            sx={{ 
              width: '100%', 
              height: '1px', 
              backgroundColor: 'divider',
              my: 2,
              mb: spacingAfter > 0 ? spacingAfterUnits : 2
            }} 
          />
        );

      default:
        // Final fallback for unknown types
        return (
          <Box key={field.id}>
            {field.description && (
              <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                {field.description}{isRequired ? ' *' : ''}
              </Typography>
            )}
            <TextField
              fullWidth
              size="small"
              value={value}
              onChange={(e) => handleFieldChange(field.code, e.target.value)}
              required={isRequired}
              disabled={!isEnabled}
              helperText={`Unknown field type: ${field.fieldType}`}
            />
          </Box>
        );
    }
  };

  // Find header section and field
  const getHeaderContent = () => {
    const headerSection = form.formSections?.find(s => s.code === 'HEADER');
    if (!headerSection) return null;
    
    const headerField = headerSection.formFields?.find(f => f.code === 'HEADER_CONTENT');
    if (!headerField) return null;
    
    const config = formService.parseConfigurationJson(headerField.configurationJson);
    return config.html || null;
  };

  const renderSection = (section: FormSection, showMargin: boolean = true, fillHeight: boolean = false) => {
    const activeFields = section.formFields
      ?.filter(field => field.isActive)
      ?.sort((a, b) => a.displayOrder - b.displayOrder) || [];

    if (activeFields.length === 0) return null;

    // Check if this is a header section (should be hidden in display mode)
    if (section.code === 'HEADER') {
      // Hide header section in display mode, only show in print
      return null;
    }

    // Handle HTML content fields that should only show in print
    const htmlField = activeFields.find(f => {
      const config = formService.parseConfigurationJson(f.configurationJson);
      return config.html && config.showInPrintOnly;
    });

    if (htmlField) {
      const config = formService.parseConfigurationJson(htmlField.configurationJson);
      return (
        <Box 
          key={section.id}
          className="print-only-content"
          sx={{ 
            display: 'none',
            '@media print': { display: 'block' }
          }}
          dangerouslySetInnerHTML={{ __html: config.html }}
        />
      );
    }

    // Calculate column width based on form.numberOfColumns
    // For numberOfColumns = 2, each column is 6 (12/2)
    // For numberOfColumns = 3, each column is 4 (12/3)
    const formNumberOfColumns = form.numberOfColumns || 2;
    const columnWidth = formNumberOfColumns > 0 ? 12 / formNumberOfColumns : 12;

    // Check if this section has independent columns (each column has its own displayOrder sequence)
    // This happens when fields with different columnNumbers have overlapping or independent displayOrder values
    const fieldsByColumn: { [key: number]: FormField[] } = {};
    const fullWidthFields: FormField[] = [];
    
    activeFields.forEach((field) => {
      // Check if field has fieldOnNextLine - if so, treat as full-width
      const fieldConfig = formService.parseConfigurationJson(field.configurationJson);
      const hasFieldOnNextLine = fieldConfig?.fieldOnNextLine || false;
      
      const fieldColumn = hasFieldOnNextLine ? 0 : (field.columnNumber || 0);
      if (fieldColumn === 0) {
        fullWidthFields.push(field);
      } else {
        if (!fieldsByColumn[fieldColumn]) {
          fieldsByColumn[fieldColumn] = [];
        }
        fieldsByColumn[fieldColumn].push(field);
      }
    });
    
    // Check if columns are independent (each column has its own displayOrder sequence starting from 1)
    // This happens when multiple columns each have fields starting from displayOrder 1
    // For example: Column 1 has displayOrder [1,2,3], Column 2 has [1,2,3], Column 3 has [1,2,3]
    // Only apply this logic for sections that are actually split into columns, not full-width sections
    const hasIndependentColumns = section.columnNumber > 0 && Object.keys(fieldsByColumn).length > 1 &&
      Object.values(fieldsByColumn).every(columnFields => {
        // Check if each column has fields with displayOrder starting from 1 (or very close like 1-2)
        const sortedOrders = columnFields.map(f => f.displayOrder).sort((a, b) => a - b);
        return sortedOrders.length > 0 && sortedOrders[0] <= 2; // Must start from displayOrder 1 or 2
      });
    
    // Group fields into rows based on displayOrder and columnNumber
    // Fields with columnNumber > 0 that appear consecutively should be on the same row
    const fieldRows: FormField[][] = [];
    
    if (hasIndependentColumns) {
      // Render as independent columns - each column stacks vertically
      // Just push full-width fields as rows, and columns will be handled separately
      fullWidthFields.forEach(field => {
        fieldRows.push([field]);
      });
    } else {
      // Group fields into rows: each row can have multiple fields as long as they have unique columnNumbers
      // Start a new row when a field has a columnNumber that already exists in the current row
      let currentRow: FormField[] = [];
      const columnsInCurrentRow = new Set<number>();

      activeFields.forEach((field) => {
        // Check if field has fieldOnNextLine - if so, treat as full-width
        const fieldConfig = formService.parseConfigurationJson(field.configurationJson);
        const hasFieldOnNextLine = fieldConfig?.fieldOnNextLine || false;

        const fieldColumn = hasFieldOnNextLine ? 0 : (field.columnNumber || 0);

        if (fieldColumn === 0) {
          // Full-width field - finish current row and add this as a separate row
          if (currentRow.length > 0) {
            fieldRows.push(currentRow);
            currentRow = [];
            columnsInCurrentRow.clear();
          }
          fieldRows.push([field]); // Full-width field is its own row
        } else {
          // Column field - check if this column is already used in current row
          if (columnsInCurrentRow.has(fieldColumn)) {
            // Start a new row (this column number already exists in current row)
            fieldRows.push(currentRow);
            currentRow = [field];
            columnsInCurrentRow.clear();
            columnsInCurrentRow.add(fieldColumn);
          } else {
            // Add to current row
            currentRow.push(field);
            columnsInCurrentRow.add(fieldColumn);
          }
        }
      });

      // Don't forget the last row
      if (currentRow.length > 0) {
        fieldRows.push(currentRow);
      }

    }

    return (
      <Paper key={section.id} sx={{ p: 3, mb: showMargin ? 3 : 0, height: fillHeight ? '100%' : 'auto' }}>
        <Typography variant="h6" gutterBottom>
          {section.description}
        </Typography>
        <Grid container spacing={2}>
          {hasIndependentColumns ? (
            // Render independent columns - each column stacks vertically
            // But we need to interleave full-width fields based on displayOrder
            (() => {
              // Merge all fields (column and full-width) and sort by displayOrder
              const allFields = [...activeFields].sort((a, b) => a.displayOrder - b.displayOrder);
              
              // Group fields into rows based on displayOrder
              // Full-width fields (columnNumber 0) are their own row
              // Column fields with the same displayOrder are in the same row
              const rows: Array<{ type: 'columns' | 'fullwidth', fields: FormField[], displayOrder: number }> = [];
              let currentRowFields: FormField[] = [];
              let currentRowDisplayOrder = -1;
              let currentRowType: 'columns' | 'fullwidth' | null = null;
              
              for (const field of allFields) {
                const fieldConfig = formService.parseConfigurationJson(field.configurationJson);
                const hasFieldOnNextLine = fieldConfig?.fieldOnNextLine || false;
                const fieldColumn = hasFieldOnNextLine ? 0 : (field.columnNumber || 0);
                
                if (fieldColumn === 0) {
                  // Full-width field - finish current row if any, then add this as its own row
                  if (currentRowFields.length > 0 && currentRowType === 'columns') {
                    rows.push({ type: 'columns', fields: currentRowFields, displayOrder: currentRowDisplayOrder });
                    currentRowFields = [];
                  }
                  rows.push({ type: 'fullwidth', fields: [field], displayOrder: field.displayOrder });
                  currentRowType = null;
                } else {
                  // Column field
                  if (currentRowType === null || (currentRowType === 'columns' && field.displayOrder !== currentRowDisplayOrder)) {
                    // Finish previous row if it exists
                    if (currentRowFields.length > 0 && currentRowType !== null) {
                      rows.push({ type: currentRowType, fields: currentRowFields, displayOrder: currentRowDisplayOrder });
                    }
                    currentRowFields = [field];
                    currentRowDisplayOrder = field.displayOrder;
                    currentRowType = 'columns';
                  } else {
                    // Add to current row
                    currentRowFields.push(field);
                    currentRowDisplayOrder = field.displayOrder;
                    currentRowType = 'columns';
                  }
                }
              }
              
              // Don't forget the last row
              if (currentRowFields.length > 0 && currentRowType) {
                rows.push({ type: currentRowType, fields: currentRowFields, displayOrder: currentRowDisplayOrder });
              }
              
              // Render rows in order
              return rows.map((row, rowIndex) => {
                if (row.type === 'fullwidth') {
                  return (
                    <Grid item xs={12} key={`row-${rowIndex}`}>
                      <Box sx={{ mb: 2 }}>
                        {renderField(row.fields[0], false)}
                      </Box>
                    </Grid>
                  );
                } else {
                  // Column row - render fields in their respective columns
                  return (
                    <Grid container item xs={12} spacing={2} key={`row-${rowIndex}`}>
                      {Array.from({ length: formNumberOfColumns }, (_, i) => i + 1).map(columnNum => {
                        const columnFields = row.fields.filter(f => {
                          const fieldConfig = formService.parseConfigurationJson(f.configurationJson);
                          const hasFieldOnNextLine = fieldConfig?.fieldOnNextLine || false;
                          const fieldCol = hasFieldOnNextLine ? 0 : (f.columnNumber || 0);
                          return fieldCol === columnNum;
                        });
                        
                        return (
                          <Grid item xs={12} sm={columnWidth} key={`col-${columnNum}`}>
                            {(() => {
                              // Check if any field in this row has a description
                              const anyFieldHasDescription = row.fields.some(f => f.description && f.description.trim() !== '');
                              return columnFields.map((field) => (
                                <Box key={field.id} sx={{ mb: 2 }}>
                                  {renderField(field, anyFieldHasDescription)}
                                </Box>
                              ));
                            })()}
                          </Grid>
                        );
                      })}
                    </Grid>
                  );
                }
              });
            })()
          ) : (
            // Original row-based rendering
            fieldRows.map((rowFields, rowIndex) => {
              // Check if this is a full-width row (single field with columnNumber 0)
              const isFullWidth = rowFields.length === 1 && (rowFields[0].columnNumber === 0 || !rowFields[0].columnNumber);
              
              if (isFullWidth) {
                // Full-width field
                return (
                  <Grid item xs={12} key={`row-${rowIndex}`}>
                    <Box sx={{ mb: 2 }}>
                      {renderField(rowFields[0], false)}
                    </Box>
                  </Grid>
                );
              } else {
                // Row with multiple columns - render fields side by side
                // Find the maximum column number used in this row to determine how many columns to render
                const maxColumnNumber = Math.max(...rowFields.map(f => f.columnNumber || 0));
                const actualColumns = Math.max(maxColumnNumber, formNumberOfColumns);
                const actualColumnWidth = actualColumns > 0 ? 12 / actualColumns : 12;
                
                return (
                  <Grid container item xs={12} spacing={2} key={`row-${rowIndex}`}>
                    {Array.from({ length: actualColumns }, (_, i) => i + 1).map(columnNum => {
                      // Find field(s) for this column number - use exact match, no wrapping
                      const columnFields = rowFields.filter(f => {
                        const fieldCol = f.columnNumber || 0;
                        return fieldCol === columnNum;
                      });
                      
                      if (columnFields.length === 0) {
                        // Empty column - render empty space for alignment
                        return (
                          <Grid item xs={12} sm={actualColumnWidth} key={`col-${columnNum}`}>
                            <Box sx={{ mb: 2 }} />
                          </Grid>
                        );
                      }
                      
                      return (
                        <Grid item xs={12} sm={actualColumnWidth} key={`col-${columnNum}`}>
                          {(() => {
                            // Check if any field in this row has a description
                            const anyFieldHasDescription = rowFields.some(f => f.description && f.description.trim() !== '');
                            return columnFields.map((field) => (
                              <Box key={field.id} sx={{ mb: 2 }}>
                                {renderField(field, anyFieldHasDescription)}
                              </Box>
                            ));
                          })()}
                        </Grid>
                      );
                    })}
                  </Grid>
                );
              }
            })
          )}
        </Grid>
      </Paper>
    );
  };


  const activeSections = form.formSections
    ?.filter(section => section.isActive)
    ?.sort((a, b) => a.displayOrder - b.displayOrder) || [];

  // Merge and sort all sections by displayOrder
  const allSections = [...activeSections].sort((a, b) => a.displayOrder - b.displayOrder);

  // Get the number of columns from the form, defaulting to 2 if not specified
  const numberOfColumns = form.numberOfColumns || 2;

  // Organize sections into independent columns and full-width sections
  const organizeSections = () => {
    const sectionsByColumn: { [key: number]: FormSection[] } = {};
    const fullWidthSections: FormSection[] = [];
    
    // Initialize columns based on numberOfColumns
    for (let i = 1; i <= numberOfColumns; i++) {
      sectionsByColumn[i] = [];
    }
    
    allSections.forEach((section) => {
      if (section.columnNumber === 0) {
        fullWidthSections.push(section);
      } else {
        // Only place sections in valid columns (1 to numberOfColumns)
        // If section.columnNumber exceeds numberOfColumns, wrap it around
        const col = ((section.columnNumber - 1) % numberOfColumns) + 1;
        sectionsByColumn[col].push(section);
      }
    });
    
    return { sectionsByColumn, fullWidthSections };
  };

  const { sectionsByColumn, fullWidthSections } = organizeSections();

  return (
    <Box className="form-renderer-container">
      {/* PDF Generation Button - Only show if form is printable */}
      {form.isPrintable && (
        <Box className="print-button-container" display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {form.description}
          </Typography>
          <Box display="flex" alignItems="center">
            <IconButton
              onClick={handleGenerateInteractivePDF}
              color="primary"
              title={isLoadingResponses ? "Loading form data..." : "Download"}
              disabled={loading || isLoadingResponses}
              sx={{
                '@media print': { display: 'none' }
              }}
            >
              {loading || isLoadingResponses ? <CircularProgress size={24} /> : <PdfIcon />}
            </IconButton>
            {isLoadingResponses && (
              <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                Loading form data...
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {/* Render sections in displayOrder with proper column grouping */}
      <Box>
        {(() => {
          const result: JSX.Element[] = [];
          let currentColumnSections: FormSection[] = [];
          
          allSections.forEach((section, index) => {
            if (section.columnNumber === 0) {
              // Full-width section - render any accumulated column sections first
              if (currentColumnSections.length > 0) {
                result.push(
                  <Box className="test1"  key={`columns-${index}`} sx={{ display: 'flex', gap: 3, alignItems: 'stretch', mb: 3 }}>
                    {Array.from({ length: numberOfColumns }, (_, i) => i + 1).map(columnNum => {
                      const colSections = sectionsByColumn[columnNum]?.filter(s => 
                        currentColumnSections.some(segSec => segSec.id === s.id)
                      ) || [];
                      
                      return (
                        <Box className="test2" key={columnNum} sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                          {colSections.map((colSection, colIndex) => {
                            const isLastInColumn = colIndex === colSections.length - 1;
                            return (
                              <Box className="test3" key={colSection.id} sx={{ mb: isLastInColumn ? 0 : 2, flex: isLastInColumn ? 1 : 0 }}>
                                {renderSection(colSection, false, isLastInColumn)}
                              </Box>
                            );
                          })}
                        </Box>
                      );
                    })}
                  </Box>
                );
                currentColumnSections = [];
              }
              
              // Render the full-width section
              var test4str = "test4-" + section.code;
              result.push(
                <Box className={test4str} key={section.id} sx={{ width: '100%', mb: 3, mt: 3 }}>
                  {renderSection(section, true)}
                </Box>
              );
            } else {
              // Column section - add to current group
              currentColumnSections.push(section);
            }
          });
          
          // Render any remaining column sections
          if (currentColumnSections.length > 0) {
            result.push(
              <Box key="final-columns" sx={{ display: 'flex', gap: 3, alignItems: 'stretch' }}>
                {Array.from({ length: numberOfColumns }, (_, i) => i + 1).map(columnNum => {
                  const colSections = sectionsByColumn[columnNum]?.filter(s => 
                    currentColumnSections.some(segSec => segSec.id === s.id)
                  ) || [];
                  
                  return (
                    <Box key={columnNum} sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                      {colSections.map((colSection, colIndex) => {
                        const isLastInColumn = colIndex === colSections.length - 1;
                        return (
                          <Box key={colSection.id} sx={{ mb: isLastInColumn ? 0 : 2, flex: isLastInColumn ? 1 : 0 }}>
                            {renderSection(colSection, false, isLastInColumn)}
                          </Box>
                        );
                      })}
                    </Box>
                  );
                })}
              </Box>
            );
          }
          
          return result;
        })()}

        {success && (
          <Alert severity="success" sx={{ mt: 3 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}
      </Box>

      {!readOnly && (
        <Box display="flex" gap={2} mt={3} sx={{ '@media print': { display: 'none' } }}>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Submitting...' : 'Submit Form'}
          </Button>
          {onCancel && (
            <Button variant="outlined" onClick={onCancel}>
              Cancel
            </Button>
          )}
        </Box>
      )}

    </Box>
  );
};

export default FormRenderer;
